import type * as React from "react";
import type { IconName } from "../icon/Icon";
import { Icon } from "../icon/Icon";

/**
 * Spinner / Tag / Alert, ported from the Wanted Design System bundle
 * (components/feedback/*, components/data-display/Tag.jsx). Grouped in one file
 * because each is a handful of lines and they're always imported together.
 */

// ---- Spinner ----

export interface SpinnerProps {
  /** Diameter in px. @default 24 */
  size?: number;
  thickness?: number;
  color?: string;
  track?: string;
  style?: React.CSSProperties;
}

/**
 * Purely decorative — the loading *state* is announced by the visible text next
 * to it (see LoadingState), so the spinner itself stays out of the a11y tree
 * rather than faking a progressbar role it can't populate.
 */
export function Spinner({
  size = 24,
  thickness,
  color = "var(--primary-normal)",
  track = "var(--fill-strong)",
  style,
}: SpinnerProps) {
  const width = typeof thickness === "number" ? thickness : Math.max(2, Math.round(size / 10));
  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-block",
        width: size,
        height: size,
        borderRadius: "50%",
        border: `${width}px solid ${track}`,
        borderTopColor: color,
        animation: "wds-spin 0.7s linear infinite",
        boxSizing: "border-box",
        ...style,
      }}
    >
      <style>{"@keyframes wds-spin{to{transform:rotate(360deg)}}"}</style>
    </span>
  );
}

// ---- Tag ----

export type TagColor = "neutral" | "primary" | "positive" | "negative" | "cautionary" | "violet";

export interface TagProps {
  /** @default "neutral" */
  color?: TagColor;
  /** @default "weak" */
  variant?: "weak" | "solid" | "outline";
  /** @default "medium" */
  size?: "medium" | "small";
  children?: React.ReactNode;
  style?: React.CSSProperties;
}

const TAG_COLORS: Record<TagColor, { fg: string; weak: string; solid: string }> = {
  neutral: { fg: "var(--label-neutral)", weak: "var(--fill-normal)", solid: "var(--label-neutral)" },
  primary: { fg: "var(--primary-normal)", weak: "var(--accent-background-light-blue)", solid: "var(--primary-normal)" },
  positive: { fg: "var(--status-positive)", weak: "var(--surface-positive)", solid: "var(--status-positive)" },
  negative: { fg: "var(--status-negative)", weak: "var(--surface-negative)", solid: "var(--status-negative)" },
  cautionary: {
    fg: "var(--status-cautionary)",
    weak: "var(--surface-cautionary)",
    solid: "var(--status-cautionary)",
  },
  violet: {
    fg: "var(--accent-foreground-violet)",
    weak: "var(--accent-background-violet)",
    solid: "var(--accent-foreground-violet)",
  },
};

const TAG_SIZES = {
  medium: { h: 24, padX: 8, font: "var(--font-size-label-2)" },
  small: { h: 20, padX: 6, font: "var(--font-size-caption-1)" },
} as const;

export function Tag({ children, color = "neutral", variant = "weak", size = "medium", style }: TagProps) {
  const c = TAG_COLORS[color] ?? TAG_COLORS.neutral;
  const s = TAG_SIZES[size] ?? TAG_SIZES.medium;
  const look =
    variant === "solid"
      ? { background: c.solid, color: "var(--static-white)", boxShadow: "none" }
      : variant === "outline"
        ? { background: "transparent", color: c.fg, boxShadow: `inset 0 0 0 1px ${c.fg}` }
        : { background: c.weak, color: c.fg, boxShadow: "none" };

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        height: s.h,
        padding: `0 ${s.padX}px`,
        borderRadius: "var(--radius-xs)",
        fontFamily: "var(--font-sans)",
        fontSize: s.font,
        fontWeight: 600,
        lineHeight: 1,
        whiteSpace: "nowrap",
        ...look,
        ...style,
      }}
    >
      {children}
    </span>
  );
}

// ---- Alert ----

export type AlertTone = "info" | "positive" | "cautionary" | "negative";

export interface AlertProps {
  /** @default "info" */
  tone?: AlertTone;
  title?: string;
  children?: React.ReactNode;
  action?: React.ReactNode;
  style?: React.CSSProperties;
}

const ALERT_TONES: Record<AlertTone, { icon: IconName; fg: string; bg: string }> = {
  info: { icon: "circleInfoFill", fg: "var(--primary-normal)", bg: "var(--accent-background-light-blue)" },
  positive: { icon: "circleCheckFill", fg: "var(--status-positive)", bg: "var(--surface-positive)" },
  cautionary: {
    icon: "triangleExclamationFill",
    fg: "var(--status-cautionary)",
    bg: "var(--surface-cautionary)",
  },
  negative: { icon: "circleCloseFill", fg: "var(--status-negative)", bg: "var(--surface-negative)" },
};

export function Alert({ tone = "info", title, children, action, style }: AlertProps) {
  const t = ALERT_TONES[tone] ?? ALERT_TONES.info;
  return (
    <div
      role="alert"
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        padding: 16,
        borderRadius: "var(--radius-md)",
        background: t.bg,
        fontFamily: "var(--font-sans)",
        ...style,
      }}
    >
      <Icon name={t.icon} size={20} style={{ color: t.fg, flex: "none", marginTop: 1 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        {title && (
          <div
            style={{
              fontSize: "var(--font-size-label-1)",
              lineHeight: "var(--line-height-label-1)",
              fontWeight: 600,
              color: "var(--label-normal)",
              marginBottom: children ? 2 : 0,
            }}
          >
            {title}
          </div>
        )}
        {children && (
          <div
            style={{
              fontSize: "var(--font-size-label-2)",
              lineHeight: "var(--line-height-label-2)",
              color: "var(--label-alternative)",
            }}
          >
            {children}
          </div>
        )}
      </div>
      {action}
    </div>
  );
}
